<template>
  <div class="flex items-center justify-center p-0 w-full"
      @dragenter.prevent="onDragEnter"
      @dragleave.prevent="onDragLeave"
      @dragover.prevent="onDragOver"
      @drop.prevent="onDrop">
    <!-- Global Full Screen Drag Overlay -->
     <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div 
        v-if="isDragging"
        class="fixed inset-0 z-[100] bg-[#2EB2A0]/95 backdrop-blur-sm flex flex-col items-center justify-center pointer-events-none"
      >
        <div class="bg-white/20 p-8 rounded-full mb-8 backdrop-blur-sm shadow-2xl">
           <span class="material-symbols-outlined text-white text-[100px]">cloud_upload</span>
        </div>
        <h2 class="text-white text-5xl font-bold tracking-tight drop-shadow-sm">Drop PDF here</h2>
      </div>
    </transition>

    <!-- Dropzone -->
    <div 
      v-if="pages.length === 0" 
      @click="fileInput.click()" 
      class="w-full max-w-[1000px] mt-7 transition-all duration-300 cursor-pointer flex flex-col items-center justify-center text-center relative group p-0"
    >
      <!-- New UI Structure -->
      <div class="absolute -inset-1 bg-gradient-to-r from-[#2EB2A0] to-[#2EB2A0]/60 rounded-2xl blur opacity-10 group-hover:opacity-25 transition duration-1000 group-hover:duration-200"></div>
      <div class="relative w-full h-[350px] bg-white border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center gap-6 transition-all hover:border-[#2EB2A0]/50">
        <div class="size-20 bg-[#2EB2A0]/10 rounded-full flex items-center justify-center text-[#2EB2A0] mt-5">
           <span class="material-symbols-outlined text-[48px]">cloud_upload</span>
        </div>
        
        <div class="space-y-1">
            <h3 class="text-xl md:text-xl font-semibold text-[#16304D]">Drag &amp; Drop your PDF here</h3>
            <p class="text-sm md:text-md font-normal text-slate-500">or click the button below to browse your files</p>
        </div>

        <button @click.stop="fileInput.click()" class="bg-[#2EB2A0] text-white text-sm font-semibold px-6 py-2 rounded-xl hover:bg-[#279B8C] shadow-xl shadow-[#2EB2A0]/20 transition-all duration-500 ease-in-out transform hover:-translate-y-1">
            Select PDF Files
        </button>

        <div class="flex flex-wrap justify-center items-center gap-4 md:gap-6 text-[8px] font-medium text-slate-400 uppercase tracking-widest mt-2">
             <span class="flex items-center gap-1.5"><span class="material-symbols-outlined text-[12px]">lock</span> Encrypted</span>
             <span class="flex items-center gap-1.5"><span class="material-symbols-outlined text-[12px]">verified_user</span> Private</span>
             <span class="flex items-center gap-1.5"><span class="material-symbols-outlined text-[12px]">cloud_done</span> Auto-Delete</span>
        </div>
      </div>
    </div>
    <!-- Main Editor -->
    <div v-else class="fixed inset-0 top-0 z-50 flex flex-col h-screen bg-[#F3F4F6]">
        <!-- Top Navigation Bar -->
        <!-- Top Navigation Bar -->
        <header class="flex items-center justify-between whitespace-nowrap border-b border-solid border-slate-200 bg-white px-4 md:px-6 py-2 md:py-3 shrink-0 shadow-sm z-20">
            <div class="flex items-center gap-3 md:gap-4">
                <a href="/" class="flex items-center gap-2">
                     <img src="https://www.organizepdf.org/logo.png" alt="Logo" class="h-8 md:h-12 w-auto">
                </a>
                <div>
                    <h2 class="text-sm md:text-lg font-semibold leading-tight text-[#16304D]">
                        <a href="/">Organize PDF</a>
                    </h2>
                    <div class="flex items-center gap-2 text-[10px] md:text-xs text-slate-500">
                        <span class="hidden xs:inline">Documents</span>
                        <span class="material-symbols-outlined text-[14px] hidden xs:inline">chevron_right</span>
                        <span class="font-medium text-[#16304D] max-w-[100px] md:max-w-none truncate">{{ fileInfo.fileTextDisplay }} ({{ pages.length }})</span>
                    </div>
                </div>
            </div>
            <div class="flex items-center gap-2 md:gap-6">
                <button @click="previewSelected" class="flex items-center justify-center gap-2 rounded-lg h-9 md:h-10 px-2 md:px-4 bg-white hover:bg-[#2EB2A0]/20 border border-slate-200 text-sm font-semibold shadow-sm hover:bg-slate-50 text-[#16304D] transition-colors">
                    <span class="material-symbols-outlined text-lg">visibility</span>
                    <span class="hidden md:inline">Preview</span>
                </button>
                 <button @click="resetAll" class="flex items-center justify-center gap-2 rounded-lg h-9 md:h-10 px-2 md:px-4 bg-red-50 border border-red-200 text-sm font-semibold shadow-sm hover:bg-red-100 text-red-600 transition-colors">
                    <span class="material-symbols-outlined text-lg">restart_alt</span>
                    <span class="hidden md:inline">Start Over</span>
                </button>
            </div>
        </header>

        <div class="flex flex-1 overflow-hidden relative">
            <!-- Sidebar (Left - Tool Palette) -->
            <!--<aside class="w-16 flex flex-col items-center py-4 border-r border-slate-200 bg-white gap-4 z-10 hidden md:flex">
                <button class="p-2 text-[#2EB2A0] bg-[#2EB2A0]/10 rounded-lg shadow-sm" title="Select Tool">
                    <span class="material-symbols-outlined">near_me</span>
                </button>
                <button class="p-2 text-slate-400 hover:bg-slate-100 rounded-lg transition" title="Hand Tool">
                    <span class="material-symbols-outlined">pan_tool</span>
                </button>
                <div class="w-8 h-px bg-slate-200"></div>
                <button class="p-2 text-slate-400 hover:bg-slate-100 rounded-lg transition" title="Add Text">
                    <span class="material-symbols-outlined">title</span>
                </button>
                <button class="p-2 text-slate-400 hover:bg-slate-100 rounded-lg transition" title="Draw">
                    <span class="material-symbols-outlined">draw</span>
                </button>
            </aside>-->

            <!-- Main Editor Workspace -->
            <main ref="scrollContainer" class="flex-1 overflow-y-auto bg-[#F3F4F6] p-3 md:p-8 relative pb-3 md:pb-8">

                <div class="max-w-7xl mx-auto h-full min-h-[500px]">
                    <!-- Draggable Grid -->
                    <draggable 
                        v-model="pages" 
                        item-key="uid" 
                        class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 md:gap-6 pb-20"
                        ghost-class="opacity-40"
                        handle=".drag-handle"
                        :delay="100"
                        :delay-on-touch-only="true"
                        :animation="200">
                        <template #item="{ element, index }">
                             <div class="thumbnail-card relative group transition-all duration-200 transform touch-manipulation settings-no-select">
                                <div class="h-52 md:h-56 bg-white rounded-lg border-2 cursor-move relative transition-all hover:shadow-xl drag-handle"
                                    :style="element.color ? { borderColor: element.color.border } : {}"
                                    :class="!element.color ? 'border-slate-200 hover:border-[#2EB2A0]' : ''" >
                                     <!-- 1. Clipping Layer (For Image and Banner) -->
                                     <div class="absolute inset-0 overflow-hidden rounded-lg z-0">
                                         <!-- Color Accent Stripe (top) -->
                                         <div v-if="element.color" class="absolute top-0 left-0 right-0 h-1 z-10" :style="{ backgroundColor: element.color.stripe }"></div>
                                         <div class="absolute inset-0 flex items-center justify-center" :style="element.color ? { backgroundColor: element.color.bg } : { backgroundColor: 'white' }">
                                             <!-- Thumbnail Image -->
                                              <img 
                                                 v-show="!element.isLoading && element.thumbnail && !element.isBlank"
                                                 :src="element.thumbnail" 
                                            class="w-full h-full object-contain p-4 transition-transform duration-200 select-none"
                                            :style="{ 
                                                transform: `rotate(${element.rotation}deg)` 
                                            }"
                                            loading="lazy"
                                            draggable="false"/>
                                        
                                        <!-- Blank Page Visual (Document-like) -->
                                         <div v-if="element.isBlank" class="w-full h-full flex items-center justify-center p-5">
                                             <div class="aspect-[1/1.41] h-full bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-slate-100 rounded-sm"></div>
                                         </div>
                                         </div>

                                         <!-- Filename Banner -->
                                         <div class="absolute bottom-0 left-0 right-0 h-10 group/info z-20">
                                             <!-- Default Page Number (per-file) -->
                                             <div class="absolute inset-0 flex justify-center items-end pb-1 transition-opacity duration-200 group-hover/info:opacity-0 z-10">
                                                 <span class="text-[10px] font-bold tracking-tighter" :style="element.color ? { color: element.color.border } : { color: '#94a3b8' }">{{ element.isBlank ? 'Blank' : (element.filePageNum || element.pageNum) }}</span>
                                             </div>

                                             <!-- Filename Banner (Slides up on hover) -->
                                             <div class="absolute bottom-0 left-0 right-0 bg-white border-t border-slate-100 px-2 py-1 transform translate-y-full group-hover/info:translate-y-0 transition-transform duration-300 ease-out z-20 hidden md:block">
                                                 <p class="text-[10px] font-semibold text-center text-[#16304D] truncate leading-tight mb-[-2px]">{{ element.fileName }}</p>
                                                 <p class="text-[9px] font-normal text-center text-slate-500 mb-[-2px]">{{ element.isBlank ? 'Blank Page' : 'Page ' + (element.filePageNum || element.pageNum) }}</p>
                                             </div>
                                         </div>
                                     </div>

                                     <!-- Non-Clipping Layer: Loader + Actions + Insert Buttons -->
                                     <div class="absolute inset-0 z-10 pointer-events-none">
                                         <!-- Individual Loader -->
                                         <div v-if="element.isLoading" class="absolute inset-0 flex flex-col items-center justify-center bg-slate-50 rounded-lg pointer-events-auto">
                                             <div class="animate-spin rounded-full h-8 w-8 border-4 border-[#2EB2A0]/30 border-t-[#2EB2A0] mb-2"></div>
                                             <span class="text-[10px] font-bold text-[#2EB2A0] uppercase tracking-wide">Processing...</span>
                                         </div>

                                         <!-- Floating Actions Bar (Desktop: Hover, Mobile: Always) -->
                                         <div class="absolute top-2 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur-sm px-1.5 py-1 rounded-full border border-slate-200 shadow-md flex items-center gap-1 z-30 transition-all duration-200 pointer-events-auto md:opacity-0 md:group-hover:opacity-100">
                                             <button 
                                                 @click.stop="rotatePage(element, -90)" 
                                                 class="w-7 h-7 flex items-center justify-center bg-white text-slate-700 rounded-full hover:bg-[#2EB2A0] hover:text-white transition-colors border border-slate-100 shadow-sm" 
                                                 title="Rotate Left"
                                             >
                                                 <span class="material-symbols-outlined text-[16px]">rotate_left</span>
                                             </button>
                                             <button 
                                                 @click.stop="rotatePage(element, 90)" 
                                                 class="w-7 h-7 flex items-center justify-center bg-white text-slate-700 rounded-full hover:bg-[#2EB2A0] hover:text-white transition-colors border border-slate-100 shadow-sm" 
                                                 title="Rotate Right"
                                             >
                                                 <span class="material-symbols-outlined text-[16px]">rotate_right</span>
                                             </button>
                                             <button 
                                                 @click.stop="duplicatePage(element)" 
                                                 class="w-7 h-7 flex items-center justify-center bg-white text-slate-700 rounded-full hover:bg-[#2EB2A0] hover:text-white transition-colors border border-slate-100 shadow-sm" 
                                                 title="Duplicate"
                                             >
                                                 <span class="material-symbols-outlined text-[16px]">content_copy</span>
                                             </button>
                                             <button 
                                                 @click.stop="deletePage(element)" 
                                                 class="w-7 h-7 flex items-center justify-center bg-white text-red-500 rounded-full hover:bg-red-500 hover:text-white transition-colors border border-slate-100 shadow-sm" 
                                                 title="Delete"
                                             >
                                                 <span class="material-symbols-outlined text-[16px]">delete</span>
                                             </button>
                                         </div>

                                        <!-- Blank Page Insert Buttons (Premium UI) -->
                                        <div class="absolute inset-y-0 left-0 w-6 flex items-center justify-start -ml-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-300 transform translate-x-0 md:-translate-x-2 md:group-hover:translate-x-0 pointer-events-auto">
                                            <button 
                                                @click.stop="addBlankPage(index, 'left')"
                                                class="relative group/btn size-6 bg-white text-black rounded-r-full flex items-center justify-center shadow-lg hover:w-8 transition-all"
                                            >
                                                <div class="absolute left-full ml-2 px-2 py-1 bg-slate-900 text-white text-[9px] font-bold rounded shadow-xl opacity-0 group-hover/btn:opacity-100 transition-all duration-200 pointer-events-none z-50 whitespace-nowrap transform -translate-x-1 group-hover:translate-x-0">
                                                     Add Blank Left
                                                     <div class="absolute top-1/2 -translate-y-1/2 -left-1 w-2 h-2 bg-slate-900 rotate-45"></div>
                                                 </div>
                                                <span class="material-symbols-outlined text-[15px]">add</span>
                                            </button>
                                        </div>
                                        <div class="absolute inset-y-0 right-0 w-6 flex items-center justify-end -mr-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-300 transform translate-x-0 md:translate-x-2 md:group-hover:translate-x-0 pointer-events-auto">
                                            <button 
                                                @click.stop="addBlankPage(index, 'right')"
                                                class="relative group/btn size-6 bg-white text-black rounded-l-full flex items-center justify-center shadow-lg hover:w-8 transition-all"
                                            >
                                                <div class="absolute right-full mr-2 px-2 py-1 bg-slate-900 text-white text-[9px] font-bold rounded shadow-xl opacity-0 group-hover/btn:opacity-100 transition-all duration-200 pointer-events-none z-50 whitespace-nowrap transform translate-x-1 group-hover:translate-x-0">
                                                     Add Blank Right
                                                     <div class="absolute top-1/2 -translate-y-1/2 -right-1 w-2 h-2 bg-slate-900 rotate-45"></div>
                                                 </div>
                                                <span class="material-symbols-outlined text-[15px]">add</span>
                                            </button>
                                        </div>
                                     </div>
                                 </div>
                             </div>
                     </template>
                    </draggable>
                    
                    <!-- Empty State in Editor if something goes wrong -->
                    <div v-if="pages.length === 0 && !isProcessing" class="flex flex-col items-center justify-center h-full text-slate-400">
                        <span class="material-symbols-outlined text-6xl mb-4">note_stack</span>
                        <p>No pages to display</p>
                    </div>
                </div>
            </main>

            <!-- Sidebar (Right - Bulk Actions) DESKTOP ONLY -->
            <aside class="w-80 border-l border-slate-200 bg-white flex-col shrink-0 z-10 shadow-[-4px_0_15px_-3px_rgba(0,0,0,0.05)] hidden md:flex">
                <div class="p-6 flex flex-col flex-1 gap-6 overflow-y-auto">
                    <div>
                        <h3 class="text-xs font-bold text-slate-900 uppercase tracking-widest mb-4 flex items-center gap-2">
                             <span class="material-symbols-outlined text-[18px] ml-3">layers</span>
                             Bulk Actions
                        </h3>
                        <div class="flex flex-col gap-3">
                            <button @click="addMoreFiles" class="flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 hover:bg-slate-50 hover:border-[#2EB2A0]/50 transition-all group">
                                <span class="material-symbols-outlined text-[#2EB2A0] transition-transform">add_box</span>
                                <span class="text-sm font-semibold text-slate-700">Add Files</span>
                            </button>

                            <button @click="rotateAllPages" class="flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 hover:bg-slate-50 hover:border-[#2EB2A0]/50 transition-all group">
                                <span class="material-symbols-outlined text-slate-500 group-hover:text-[#2EB2A0] transition-colors">sync</span>
                                <span class="text-sm font-semibold text-slate-700">Rotate All</span>
                            </button>

                            <button @click="sortFilesByName" class="flex items-center justify-between px-4 py-3 rounded-xl border border-slate-200 hover:bg-slate-50 hover:border-[#2EB2A0]/50 transition-all group w-full" :class="isSortingActive ? 'border-[#2EB2A0]/30 bg-[#2EB2A0]/5' : ''">
                                <div class="flex items-center gap-3">
                                    <span class="material-symbols-outlined text-slate-500 group-hover:text-[#2EB2A0] transition-colors">sort_by_alpha</span>
                                    <span class="text-sm font-semibold text-slate-700">Sort PDF Alphabetically</span>
                                </div>
                                <span class="text-xs font-bold text-[#2EB2A0] bg-[#2EB2A0]/10 px-2 py-0.5 rounded uppercase">{{ nameSortDir === 'asc' ? 'A-Z' : 'Z-A' }}</span>
                            </button>

                            <button @click="sortPagesByNumber" class="flex items-center justify-between px-4 py-3 rounded-xl border border-slate-200 hover:bg-slate-50 hover:border-[#2EB2A0]/50 transition-all group w-full" :class="isSortingActive ? 'border-[#2EB2A0]/30 bg-[#2EB2A0]/5' : ''">
                                <div class="flex items-center gap-3">
                                    <span class="material-symbols-outlined text-slate-500 group-hover:text-[#2EB2A0] transition-colors">format_list_numbered</span>
                                    <span class="text-sm font-semibold text-slate-700">Sort PDF Numerically</span>
                                </div>
                                <span class="text-xs font-bold text-[#2EB2A0] bg-[#2EB2A0]/10 px-2 py-0.5 rounded uppercase">{{ numberSortDir === 'asc' ? '1-10' : '10-1' }}</span>
                            </button>

                            <button v-if="isSortingActive" @click="resetSort" class="flex items-center gap-3 px-4 py-3 rounded-xl border border-[#2EB2A0] bg-[#2EB2A0]/10 hover:bg-[#2EB2A0]/20 transition-all group w-full">
                                <span class="material-symbols-outlined text-[#2EB2A0]">restore</span>
                                <span class="text-sm font-semibold text-[#16304D]">Original Order (Reset)</span>
                            </button>
                        </div>
                    </div>
                </div>
                
                <div class="p-6 bg-slate-50 border-t border-slate-200">
                    <div class="mb-4 flex items-center justify-between">
                        <div>
                            <p class="text-xs text-slate-500 font-semibold mb-2 uppercase tracking-widest">Document Summary</p>
                            <p class="text-lg font-bold text-[#16304D]">{{ pages.length }} {{ pages.length === 1 ? 'Page' : 'Pages' }} <span class="text-slate-400 mx-2">&middot;</span> {{ formattedTotalSize }}</p>
                        </div>
                        <div class="bg-[#2EB2A0]/10 p-2 rounded-lg">
                             <span class="material-symbols-outlined text-[#2EB2A0]">description</span>
                        </div>
                    </div>
                    
                    <button @click="saveAndDownload" :disabled="isDownloading" class="w-full bg-[#2EB2A0] text-white h-10 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 shadow-md hover:bg-[#279B8C] transition-all active:scale-95 mb-3 disabled:opacity-70 disabled:cursor-not-allowed">
                        <span v-if="isDownloading" class="animate-spin material-symbols-outlined">progress_activity</span>
                        <span v-else class="material-symbols-outlined">download</span>
                        <span>{{ isDownloading ? 'Processing...' : 'Download PDF' }}</span>
                    </button>
                    
                    <button @click="resetAll" class="w-full text-slate-500 hover:text-red-500 text-sm font-bold py-2 transition-colors flex items-center justify-center gap-1 group">
                        <span class="material-symbols-outlined text-[20px] group-hover:animate-shake">delete_sweep</span>
                        Reset All
                    </button>
                </div>
            </aside>

            <!-- MOBILE BOTTOM BAR -->
            <div class="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40 shadow-[0_-4px_15px_-3px_rgba(0,0,0,0.1)] safe-area-pb">
                <!-- Bulk Actions Toolbar (Horizontal Scrollable) -->
                <div class="flex items-center gap-2 overflow-x-auto px-4 py-2 border-b border-slate-100 no-scrollbar bg-slate-50">
                    <button @click="rotateAllPages" class="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-full text-slate-700 active:scale-95 transition-transform shrink-0">
                        <span class="material-symbols-outlined text-[16px] text-[#2EB2A0]">sync</span>
                        <span class="text-[10px] font-bold">Rotate All</span>
                    </button>
                    <button @click="sortFilesByName" class="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-full text-slate-700 active:scale-95 transition-transform shrink-0">
                        <span class="material-symbols-outlined text-[16px] text-[#2EB2A0]">sort_by_alpha</span>
                        <span class="text-[10px] font-bold">Sort Name ({{ nameSortDir === 'asc' ? 'A-Z' : 'Z-A' }})</span>
                    </button>
                    <button @click="sortPagesByNumber" class="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-full text-slate-700 active:scale-95 transition-transform shrink-0">
                        <span class="material-symbols-outlined text-[16px] text-[#2EB2A0]">format_list_numbered</span>
                        <span class="text-[10px] font-bold">Sort Number ({{ numberSortDir === 'asc' ? '1-10' : '10-1' }})</span>
                    </button>
                    <button v-if="isSortingActive" @click="resetSort" class="flex items-center gap-1.5 px-3 py-1.5 bg-[#2EB2A0]/10 border border-[#2EB2A0]/30 rounded-full text-[#16304D] active:scale-95 transition-transform shrink-0">
                        <span class="material-symbols-outlined text-[16px] text-[#2EB2A0]">restore</span>
                        <span class="text-[10px] font-bold">Reset Order</span>
                    </button>
                    <button @click="resetAll" class="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 border border-red-200 rounded-full text-red-600 active:scale-95 transition-transform shrink-0">
                        <span class="material-symbols-outlined text-[16px] text-red-500">delete_sweep</span>
                        <span class="text-[10px] font-bold">Reset All</span>
                    </button>
                </div>

                <!-- Main Actions (Add and Download) -->
                <div class="px-4 py-3 flex items-center justify-between gap-4">
                    <button @click="addMoreFiles" class="flex flex-col items-center justify-center text-slate-500 hover:text-[#2EB2A0] active:scale-95 transition-transform min-w-[45px]">
                        <div class="bg-slate-100 p-1.5 rounded-lg mb-1">
                            <span class="material-symbols-outlined text-xl text-[#2EB2A0]">add_box</span>
                        </div>
                        <span class="text-[9px] font-bold">Add Files</span>
                    </button>
                    
                    <button @click="saveAndDownload" :disabled="isDownloading" class="flex-1 bg-[#2EB2A0] text-white h-10 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 shadow-md hover:bg-[#279B8C] disabled:opacity-70 active:scale-95 transition-transform">
                         <span v-if="isDownloading" class="animate-spin material-symbols-outlined">progress_activity</span>
                         <span v-else class="material-symbols-outlined">download</span>
                         <span>Download PDF</span>
                    </button>
                </div>
            </div>

        </div>
    </div>
    <!-- Hidden file input -->
    <input
      type="file"
      ref="fileInput"
      multiple
      accept="application/pdf"
      class="hidden"
      @change="onFileChange"
    />
  </div>
  <!-- Fullscreen Preview Modal -->
  <div v-if="showPreviewModal" class="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
    <div class="w-full h-full flex flex-col overflow-hidden max-w-full max-h-full">
      <!-- Top Toolbar -->
      <div class="bg-black/90 opacity-90 px-3 md:px-6 py-3 flex items-center justify-between shadow-md z-10 shrink-0 border-b border-black/20 relative">
        <div class="flex items-center gap-2 md:gap-4 overflow-x-auto no-scrollbar z-20">
          <button @click="rotateCurrentPage(-90)" class="text-white bg-white/10 hover:bg-white/20 px-3 py-2 rounded-lg font-medium transition flex items-center gap-2 shadow-sm whitespace-nowrap backdrop-blur-sm border border-white/10">
            <RotateCcw :size="20" />
            <span class="hidden sm:inline">Rotate Left</span>
          </button>
          <button @click="rotateCurrentPage(90)" class="text-white bg-white/10 hover:bg-white/20 px-3 py-2 rounded-lg font-medium transition flex items-center gap-2 shadow-sm whitespace-nowrap backdrop-blur-sm border border-white/10">
            <RotateCw :size="20" />
            <span class="hidden sm:inline">Rotate Right</span>
          </button>
          <button @click="deleteCurrentPage" class="text-white bg-[#2EB2A0] hover:bg-[#279B8C] px-3 py-2 rounded-lg font-medium transition flex items-center gap-2 shadow-sm whitespace-nowrap border border-[#2EB2A0]">
            <Trash2 :size="20" />
            <span class="hidden sm:inline">Delete Page</span>
          </button>
        </div>

        <!-- Centered Page Counter -->
        <div class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white font-medium bg-white/10 px-3 py-2 rounded-md backdrop-blur-sm border border-white/10 shadow-sm text-sm pointer-events-none whitespace-nowrap ml-5 md:ml-0">
          Page {{ swiperActiveIndex + 1 }} / {{ previewPages.length }}
        </div>

        <button @click="showPreviewModal = false" class="ml-2 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition z-20">
          <X :size="24" />
        </button>
      </div>

      <!-- Swiper Preview -->
      <div class="flex-1 flex items-center justify-center overflow-hidden w-full h-full relative opacity-90">
            <Swiper
              :key="swiperRerenderKey"
              :modules="[Navigation, Pagination]"
              navigation
              pagination
              :space-between="20"
              :slides-per-view="1"
              class="w-full h-full flex items-center"
              @slideChange="onSlideChange"
              ref="swiperRef">
              <SwiperSlide
              v-for="(page, index) in previewPages"
              :key="page.uid + '-' + page.rotation"
              class="flex items-center justify-center h-full w-full"
            >
            <div class="flex flex-col items-center justify-center w-full h-full p-2 md:p-6 box-border">
              <!-- Image Container -->
              <div class="relative w-full h-full flex items-center justify-center overflow-hidden">
                <img
                  v-if="!page.isBlank"
                  :key="page.uid + '-' + page.rotation + '-' + (page.thumbnail ? page.thumbnail.substring(0,50) : '')"
                  :src="page.hqUrl || page.thumbnail"
                  :alt="`Page ${page.pageNum}`"
                  class="max-w-full max-h-full object-contain rounded-sm shadow-2xl mb-5 transition-transform duration-300"
                  :style="{ transform: `rotate(${page.rotation}deg)`, maxHeight: '85vh' }"
                />
                <!-- Blank Page Visual in Preview -->
                <div v-else class="w-full h-full flex items-center justify-center p-8">
                   <div class="aspect-[1/1.41] h-full max-h-[80vh] bg-white shadow-2xl border border-slate-200 rounded-sm"></div>
                </div>
              </div>
            </div>
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import draggable from 'vuedraggable'
import { PDFDocument, degrees } from 'pdf-lib'
import * as pdfjsLib from 'pdfjs-dist/build/pdf'
import { Upload, RotateCcw, RotateCw, Trash2, Download, X, Plus, Lock, ShieldCheck, Cloud } from 'lucide-vue-next'

// Set worker for pdfjs
pdfjsLib.GlobalWorkerOptions.workerSrc = '/js/pdf.worker.js';

// Swiper Correct Imports (2025 ke liye)
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Pagination, Zoom } from 'swiper/modules'   // YEH WAALA
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/zoom'

const swiperRef = ref(null)
const swiperRerenderKey = ref(0)
const swiperActiveIndex = ref(0)
const isDownloading = ref(false)
const zoomLevel = ref(100)
const scrollContainer = ref(null)

const previewSelected = () => {
  previewPages.value = pages.value
  swiperActiveIndex.value = 0
  showPreviewModal.value = true
  
  // Load HQ preview for first page
  loadHighResPreview(0)

  nextTick(() => {
    swiperRef.value?.swiper?.slideTo(0)
  })
}

const onSlideChange = (swiper) => {
  swiperActiveIndex.value = swiper.activeIndex
  loadHighResPreview(swiper.activeIndex)
}

const loadHighResPreview = async (index) => {
  const page = previewPages.value[index]
  if (!page || page.hqUrl) return

  const pdfjsDoc = pdfjsDocs.value[page.fileName]
  if (!pdfjsDoc) return

  try {
    const pdfPage = await pdfjsDoc.getPage(page.pageNum)
    
    // Calculate optimal scale for mobile/retina
    // Mobile devices usually have DPR 2 or 3.
    // We aim for a crisp render. Scale 3 or 4 is usually safe and sharp.
    // Dynamic calculation:
    const dpr = window.devicePixelRatio || 1
    const screenWidth = window.innerWidth
    
    // Get unscaled viewport
    const unscaledViewport = pdfPage.getViewport({ scale: 1, rotation: 0 }) // Use 0 to match thumbnail orientation
    
    // Determine scale needed to fill width
    // We want the image to be at least screen width * dpr
    const widthScale = (screenWidth * dpr) / unscaledViewport.width
    
    // Limit min scale to avoid blur on small PDFs and max to avoid memory kill
    // Scale 1.5 is min default, 4 is max reasonable for mobile.
    // If the PDF is huge, widthScale might be small. 
    // We'll trust a fixed high scale for consistency or dynamic if logical.
    
    // Simple robust strategy: Max of (calculated fit * 1.5) or 3.0
    const effectiveScale = Math.max(widthScale, 3.0)

    const viewport = pdfPage.getViewport({ scale: effectiveScale, rotation: 0 })

    const canvas = document.createElement('canvas')
    canvas.width = viewport.width
    canvas.height = viewport.height
    
    const ctx = canvas.getContext('2d')
    await pdfPage.render({ canvasContext: ctx, viewport }).promise

    page.hqUrl = canvas.toDataURL('image/png')
  } catch (e) {
    console.error('HQ Render failed', e)
  }
}

const rotateCurrentPage = async (deg) => {
  const page = previewPages.value[swiperActiveIndex.value];
  if (!page) return;

  page.rotation = (page.rotation + deg + 360) % 360;
};



const deleteCurrentPage = () => {
  const page = previewPages.value[swiperActiveIndex.value]
  if (!page) return

  // Remove from main pages array
  pages.value = pages.value.filter(p => p.uid !== page.uid)
  previewPages.value = previewPages.value.filter(p => p.uid !== page.uid)

  if (previewPages.value.length === 0) {
    showPreviewModal.value = false
  } else if (swiperActiveIndex.value >= previewPages.value.length) {
    swiperActiveIndex.value = previewPages.value.length - 1
    nextTick(() => {
      swiperRef.value?.swiper?.slideTo(swiperActiveIndex.value)
    })
  }
}


// Refs
const pages = ref([])


const pdfjsDocs = ref({})
const pdfLibDocs = ref({})
const previewPages = ref([])
const showPreviewModal = ref(false)
const totalFileSize = ref(0) // Track total size

// PDF Color Categorization — 10 visually DISTINCT colors (no similar looking ones)
const FILE_COLOR_PALETTE = [
  { border: '#00BCD4', bg: '#F0FEFF', stripe: '#00BCD4' }, // Cyan
  { border: '#F4811F', bg: '#FFF8F0', stripe: '#F4811F' }, // Vivid Orange
  { border: '#2ECC71', bg: '#F0FFF8', stripe: '#2ECC71' }, // Bright Green
  { border: '#E53935', bg: '#FFF5F5', stripe: '#E53935' }, // Bold Red
  { border: '#1565C0', bg: '#F0F4FF', stripe: '#1565C0' }, // Deep Blue
  { border: '#7B1FA2', bg: '#F9F0FF', stripe: '#7B1FA2' }, // Deep Purple
  { border: '#F7B731', bg: '#FFFBF0', stripe: '#F7B731' }, // Amber Yellow
  { border: '#00897B', bg: '#F0FFF9', stripe: '#00897B' }, // Dark Teal
  { border: '#795548', bg: '#FFF8F5', stripe: '#795548' }, // Brown
  { border: '#FF6B9D', bg: '#FFF0F5', stripe: '#FF6B9D' }, // Pastel Rose
  { border: '#A29BFE', bg: '#F3F0FF', stripe: '#A29BFE' }, // Lavender
  { border: '#E91E8C', bg: '#FFF0F8', stripe: '#E91E8C' }, // Hot Pink
  { border: '#00CEC9', bg: '#F0FFFD', stripe: '#00CEC9' }, // Mint Teal
  { border: '#6C5CE7', bg: '#F5F3FF', stripe: '#6C5CE7' }, // Indigo
  { border: '#E17055', bg: '#FFF4F0', stripe: '#E17055' }, // Terra Cotta
  { border: '#74B9FF', bg: '#F0F8FF', stripe: '#74B9FF' }, // Sky Blue
  { border: '#00B894', bg: '#F0FFF9', stripe: '#00B894' }, // Emerald
]
const fileColorMap = ref({}) // fileName → color object
let fileColorIndex = 0

const getFileColor = (fileName) => {
  if (!fileColorMap.value[fileName]) {
    fileColorMap.value[fileName] = FILE_COLOR_PALETTE[fileColorIndex % FILE_COLOR_PALETTE.length]
    fileColorIndex++
  }
  return fileColorMap.value[fileName]
}

const formattedTotalSize = computed(() => {
    if (totalFileSize.value === 0) return '0 KB'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(totalFileSize.value) / Math.log(k))
    return parseFloat((totalFileSize.value / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
})

// Watcher to lock body scroll
// Watcher to lock body scroll (Modal OR Editor Active)
watch([showPreviewModal, () => pages.value.length], ([modalOpen, pageCount]) => {
  if (modalOpen || pageCount > 0) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})
const previewCanvases = ref({})
const isDragging = ref(false)
const isProcessing = ref(false)
const totalPagesBeingProcessed = ref(0)
let uidCounter = 0
const fileInput = ref(null)
const currentPreviewIndex = ref(0)
const previewCanvas = ref(null)

const fileInfo = computed(() => {
  const uniqueFiles = [...new Set(pages.value.map(p => p.fileName))]
  const fileCount = uniqueFiles.length

  // Page plural
  const pageText = pages.value.length === 1 ? 'Page' : 'Pages'

  // File text logic:
  let fileTextDisplay = ''

  if (fileCount <= 2) {
    // 1 ya 2 files → names show
    fileTextDisplay = uniqueFiles.join(', ')
  } else {
    // 3+ files → sirf count show
    fileTextDisplay = `${fileCount} Files`
  }

  return { uniqueFiles, fileCount, fileTextDisplay, pageText }
})

// Dropzone options
const dropzoneOptions = {
  url: 'https://httpbin.org/post',
  autoProcessQueue: false,
  acceptedFiles: 'application/pdf',
  addRemoveLinks: false,
}

const handleDrop = (e) => {
  isDragging.value = false
  const files = Array.from(e.dataTransfer.files)
  processFiles(files)
}

const handlePaste = (e) => {
  const items = e.clipboardData?.items
  if (!items) return
  
  for (let item of items) {
    if (item.type === 'application/pdf' || item.type.startsWith('application/')) {
      const file = item.getAsFile()
      if (file && file.type === 'application/pdf') {
        processFiles([file])
        break
      }
    }
  }
}

const processFiles = async (files) => {
  const pdfFiles = files.filter(f => f.type === 'application/pdf')
  if (!pdfFiles.length) {
    alert('Please select PDF files only')
    return
  }

  isProcessing.value = true
  
  // Minimal delay to ensure loader is seen if parsing is super fast
  await new Promise(r => setTimeout(r, 300))

  try {
    for (const file of pdfFiles) {
      totalFileSize.value += file.size // Accumulate size
      const buffer = await file.arrayBuffer()

      // Load with pdf-lib
      const pdfLibDoc = await PDFDocument.load(buffer)
      pdfLibDocs.value[file.name] = pdfLibDoc

      // Load with pdfjs
      const pdfjsDoc = await pdfjsLib.getDocument({ data: buffer }).promise
      pdfjsDocs.value[file.name] = pdfjsDoc

      // Create placeholders
      const newPages = []
      const fileColor = getFileColor(file.name)
      for (let i = 1; i <= pdfjsDoc.numPages; i++) {
        newPages.push({
          uid: uidCounter++,
          fileName: file.name,
          pageNum: i,
          filePageNum: i,  // Per-file page number (resets to 1 per PDF)
          rotation: 0,
          thumbnail: '',
          hqUrl: null,
          isLoading: true,
          color: fileColor  // Assign color based on source file
        })
      }
      
      // Push to main array and capturing the starting index
      const startIndex = pages.value.length
      pages.value.push(...newPages)
      
      // IMPORTANT: reliable reactivity
      // Grab the specific items we just added from pages.value (which are now Proxies)
      // and pass THOSE to the background worker.
      const reactiveNewPages = pages.value.slice(startIndex, startIndex + newPages.length)

      // Start background rendering with the REACTIVE objects
      renderThumbnailsBackground(pdfjsDoc, reactiveNewPages)
    }

    // Auto-scroll to bottom only when new files are processed
    nextTick(() => {
      if (scrollContainer.value) {
        scrollContainer.value.scrollTo({
          top: scrollContainer.value.scrollHeight,
          behavior: 'smooth'
        });
      }
    });
  } catch (error) {
    console.error('Error processing PDF:', error)
    alert('Error processing PDF file. Please try again.')
  } finally {
    isProcessing.value = false
    totalPagesBeingProcessed.value = 0
  }
}

// Background helper to render thumbnails one by one without blocking UI
const renderThumbnailsBackground = async (pdfjsDoc, pageObjects) => {
  for (const pageObj of pageObjects) {
    try {
      // Small yield to ensure UI stays responsive
      await new Promise(resolve => setTimeout(resolve, 50))

      const { thumbnail } = await renderPage(pdfjsDoc, pageObj.pageNum, 0)
      
      // Update the reactive object directly - Vue will pick this up now
      pageObj.thumbnail = thumbnail
      pageObj.isLoading = false
    } catch (e) {
      console.error('Error rendering thumbnail:', e)
      pageObj.isLoading = false
    }
  }
}

const onFileChange = (e) => {
  const files = Array.from(e.target.files)
  if (files.length) processFiles(files)
  e.target.value = ''
}

const addMoreFiles = () => {
  fileInput.value.click()
}

const renderPage = async (pdfDoc, pageNum, rotation = 0) => {
  const page = await pdfDoc.getPage(pageNum);

  const viewport = page.getViewport({
    scale: 4.0, // HD Quality
    rotation,
  });

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({ canvasContext: ctx, viewport }).promise;

  return { thumbnail: canvas.toDataURL("image/png") };
};


// Helper for Single Page Rotation
const rotatePage = (page, deg) => {
  page.rotation = (page.rotation + deg + 360) % 360
}

// Helper for Single Page Deletion
const deletePage = (page) => {
  pages.value = pages.value.filter(p => p.uid !== page.uid)
}

// Helper for Single Page Duplication (inserts copy right after original)
const duplicatePage = (page) => {
  const idx = pages.value.findIndex(p => p.uid === page.uid)
  if (idx === -1) return
  const copy = {
    uid: uidCounter++,
    fileName: page.fileName,
    pageNum: page.pageNum,
    rotation: page.rotation,
    thumbnail: page.thumbnail,
    hqUrl: null,
    isLoading: false,
    color: page.color
  }
  pages.value.splice(idx + 1, 0, copy)
}

// Add Blank Page (Left or Right)
const addBlankPage = (index, position) => {
    const sourcePage = pages.value[index]
    const insertIndex = position === 'left' ? index : index + 1
    const blankPage = {
        uid: uidCounter++,
        fileName: sourcePage ? sourcePage.fileName : 'Blank Page',
        pageNum: 1, // Placeholder
        rotation: 0,
        thumbnail: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAwMCAO+ip1sAAAAASUVORK5CYII=', // Tiny white pixel
        hqUrl: null,
        isLoading: false,
        isBlank: true,
        color: sourcePage ? sourcePage.color : null
    }
    pages.value.splice(insertIndex, 0, blankPage)
}

// Bulk Actions
const reverseOrder = () => {
    pages.value = pages.value.reverse()
}

const rotateAllPages = () => {
    pages.value.forEach(page => {
        page.rotation = (page.rotation + 90) % 360
    })
}

// Sorting State
const nameSortDir = ref('asc')
const numberSortDir = ref('asc')
const isSortingActive = ref(false)

const applyCombinedSort = () => {
  isSortingActive.value = true
  
  // 1. Blank pages ki positions save kro aur unhe sort se bahar nikal lo
  const blanks = pages.value.map((p, i) => p.isBlank ? {p, i} : null).filter(x => x !== null);
  const pagesWithoutBlanks = pages.value.filter(p => !p.isBlank);

  // 2. Sirf non-blanks ko sort kro
  pagesWithoutBlanks.sort((a, b) => {
    // Pehle File Name se sort kro (using nameSortDir)
    const nameResult = a.fileName.localeCompare(b.fileName, undefined, { numeric: true, sensitivity: 'base' })
    if (nameResult !== 0) {
      return nameSortDir.value === 'asc' ? nameResult : -nameResult
    }
    
    // Agar file same hai, to Page Number se sort kro (using numberSortDir)
    const pageResult = a.pageNum - b.pageNum
    return numberSortDir.value === 'asc' ? pageResult : -pageResult
  })

  // 3. Sorted pages mein blanks ko wapas unki original positions pr daal do
  const sorted = [...pagesWithoutBlanks];
  blanks.forEach(b => sorted.splice(b.i, 0, b.p));
  pages.value = sorted;
}

const sortFilesByName = () => {
  nameSortDir.value = nameSortDir.value === 'asc' ? 'desc' : 'asc'
  applyCombinedSort()
}

const sortPagesByNumber = () => {
  numberSortDir.value = numberSortDir.value === 'asc' ? 'desc' : 'asc'
  applyCombinedSort()
}

const resetSort = () => {
  pages.value.sort((a, b) => a.uid - b.uid)
  isSortingActive.value = false
  nameSortDir.value = 'asc'
  numberSortDir.value = 'asc'
}

const renderPreviewPage = async (index) => {
  if (!previewPages.value[index]) return

  await nextTick()

  const canvas = previewCanvas.value
  if (!canvas) {
  console.error('Preview canvas not found')
    return
  }

  const page = previewPages.value[index]
  const pdfjsDoc = pdfjsDocs.value[page.fileName]
  if (!pdfjsDoc) {
    console.error('PDF document not loaded for preview')
    return
  }


  try {
    const pdfPage = await pdfjsDoc.getPage(page.pageNum)

    // Get container size for responsive scaling
    const container = canvas.parentElement
    const maxWidth = container.clientWidth * 0.9
    const maxHeight = container.clientHeight * 0.9

    // Get original viewport
    let viewport = pdfPage.getViewport({ scale: 1, rotation: page.rotation })
    let scale = Math.min(maxWidth / viewport.width, maxHeight / viewport.height, 3) // Max scale 3
    scale = Math.max(scale, 0.5) // Min scale

    viewport = pdfPage.getViewport({ scale, rotation: page.rotation })

    // Set canvas size
    canvas.width = viewport.width
    canvas.height = viewport.height
    canvas.style.width = `${viewport.width}px`
    canvas.style.height = `${viewport.height}px`

    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Use requestAnimationFrame to ensure rendering after resize
    requestAnimationFrame(async () => {
      try {
        const renderContext = {
          canvasContext: ctx,
          viewport: viewport,
        }
        await pdfPage.render(renderContext).promise
      } catch (err) {
        console.error('Render failed:', err)
      }
    })
  } catch (error) {
    console.error('Error rendering preview:', error)
  }
}

const previewNext = async () => {
  if (currentPreviewIndex.value < previewPages.value.length - 1) {
    currentPreviewIndex.value++
    await renderPreviewPage(currentPreviewIndex.value)
  }
}

const previewPrevious = async () => {
  if (currentPreviewIndex.value > 0) {
    currentPreviewIndex.value--
    await renderPreviewPage(currentPreviewIndex.value)
  }
}

const rotatePreviewPage = async (deg) => {
  const page = previewPages.value[currentPreviewIndex.value]
  if (!page) return

  try {
    page.isLoading = true
    page.rotation = (page.rotation + deg + 360) % 360
    
    // Update thumbnail for the page in grid
    const pdfjsDoc = pdfjsDocs.value[page.fileName]
    const { thumbnail } = await renderPage(pdfjsDoc, page.pageNum, page.rotation)
    page.thumbnail = thumbnail

    // Re-render preview with new rotation
    await renderPreviewPage(currentPreviewIndex.value)
  } catch (error) {
    console.error('[v0] Error rotating preview page:', error)
  } finally {
    page.isLoading = false
  }
}

const deletePreviewPage = () => {
  const page = previewPages.value[currentPreviewIndex.value]
  if (!page) return

  // Remove from main pages array
  pages.value = pages.value.filter(p => p.uid !== page.uid)
  previewPages.value = previewPages.value.filter(p => p.uid !== page.uid)

  // Update preview index
  if (currentPreviewIndex.value >= previewPages.value.length) {
    currentPreviewIndex.value = Math.max(0, previewPages.value.length - 1)
  }

  // Re-render or close if no pages left
  if (previewPages.value.length === 0) {
    showPreviewModal.value = false
  } else {
    renderPreviewPage(currentPreviewIndex.value)
  }
}

const createMergedPdf = async () => {
  const mergedPdf = await PDFDocument.create()
  for (const page of pages.value) {
    const origPdf = pdfLibDocs.value[page.fileName]
    const [copiedPage] = await mergedPdf.copyPages(origPdf, [page.pageNum - 1])
    if (page.rotation !== 0) {
      copiedPage.setRotation(degrees(page.rotation))
    }
    mergedPdf.addPage(copiedPage)
  }
  return await mergedPdf.save()
}

const saveAndDownload = async () => {
  try {
    isDownloading.value = true   // Loader ON

    await nextTick()             // UI freeze na ho
    await new Promise(resolve => setTimeout(resolve, 50)) // Device hang se bachao

  const bytes = await createMergedPdf()
  const blob = new Blob([bytes], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `organized-${Date.now()}.pdf`
  a.click()
  URL.revokeObjectURL(url)

  } catch (e) {
    console.error(e)
  } finally {
    isDownloading.value = false  // Loader OFF
  }
}

// Reset all pages
const resetAll = () => {
  pages.value = []
  pdfjsDocs.value = {}
  pdfLibDocs.value = {}
  totalFileSize.value = 0
  uidCounter = 0
}

// Calculate maxHeight dynamically based on 15 items
const maxHeight = computed(() => {
  // Estimate item height including padding/margin
  const itemHeight = 228 // 8rem = 128px
  const gap = 16 // Tailwind gap-4 = 1rem = 16px

  // Determine number of columns based on Tailwind breakpoints
  const width = window.innerWidth
  let columns = 2
  if (width >= 640) columns = 3
  if (width >= 768) columns = 4
  if (width >= 1024) columns = 5
  if (width >= 1280) columns = 5

  const rowsFor15Items = Math.ceil(15 / columns)
  return rowsFor15Items * itemHeight + (rowsFor15Items - 1) * gap
})

onMounted(() => {
  window.addEventListener('paste', handlePaste)
  // Prevent default browser behavior for drag and drop globally
  window.addEventListener('dragover', (e) => e.preventDefault())
  window.addEventListener('drop', (e) => e.preventDefault())
})

onUnmounted(() => {
  window.removeEventListener('paste', handlePaste)
  document.body.style.overflow = ''
})

// Global Drag & Drop Logic
let dragCounter = 0

const onDragEnter = (e) => {
  e.preventDefault()
  dragCounter++
  if (dragCounter === 1) {
    isDragging.value = true
  }
}

const onDragLeave = (e) => {
  e.preventDefault()
  dragCounter--
  if (dragCounter === 0) {
    isDragging.value = false
  }
}

const onDragOver = (e) => {
  e.preventDefault()
}

const onDrop = (e) => {
  e.preventDefault()
  isDragging.value = false
  dragCounter = 0
  const files = Array.from(e.dataTransfer.files)
  if (files.length > 0) {
      processFiles(files)
  }
}

</script>

<style scoped>
button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.75s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

.slide-up-enter-to,
.slide-up-leave-from {
  transform: translateY(0);
  opacity: 1;
}
:deep(.swiper-button-next),
:deep(.swiper-button-prev) {
  color: #2EB2A0 !important;
}

:deep(.swiper-pagination-bullet) {
  background: #2EB2A0 !important;
  opacity: 0.4;
}

:deep(.swiper-pagination-bullet-active) {
  background: #2EB2A0 !important;
  opacity: 1;
}
</style>
